import {
  type ChangeEvent,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Controller, useWatch } from "react-hook-form";
import { useLocalization } from "@fluent/react";
import {
  Alert,
  Button,
  ContentCard,
  RadioButtons,
  ResponsiveFlex,
  Row,
  TextInput,
  Spinner,
  Tooltip,
} from "@narmi/design_system";
import Account from "byzantine/src/Account";
import { modules, useFeature } from "byzantine";
import { FREQUENCIES } from "byzantine/src/Recurrence";
import { useRecipients } from "byzantine/src/dbbl/businessLogic/entities/recipients";
import {
  useCreateInternationalWireForm,
  useFetchExchangeRateQuote,
  useEffectiveConversionRate,
  validateAmountAgainstBalance,
} from "byzantine/src/dbbl/businessLogic/modules/internationalWires";
import { useDebounce } from "byzantine/src/dbbl/hooks/useDebouncedEffect.hook";
import filters from "byzantine/src/filters";
import { catcat as cc } from "cerulean";

import CurrencyAmountInput from "../../../../../cerulean/src/ReactHookForm/CurrencyAmountInput";
import ComboAccountSelector from "../../../ComboAccountSelector";
import AccountContext from "../../../contexts/AccountContext";
import RecipientSelector from "../../../transfer/RecipientSelector";
import FrequencySelector from "../../FrequencySelector";
import styles from "./InternationalWire.module.scss";
import WireReasonSelector from "./WireReasonSelector";
import LimitsDialog from "../../LimitsDialog";
import { useLimits } from "../../../contexts/LimitsContext";
import getLimitsForDisplay from "../../limitsForDisplay";
import {
  handleInternationalWireFrequencyChange,
  InternationalWireRecurrenceEndCard,
  InternationalWireStartDateField,
} from "./InternationalWireRecurrenceEndCard";

type FreeFormFxProps = {
  reasonsRes: API.InternationalWireReasonResponse;
  onAddRecipientClick: () => void;
  renderRecipientInstitutionDetails: () => ReactNode;
  onBack: () => void;
  onSubmit: () => void;
  showAdditionalDetailsScreen: boolean;
  setShowAdditionalDetailsScreen: Dispatch<SetStateAction<boolean>>;
};

const FreeFormFx = ({
  reasonsRes,
  onAddRecipientClick,
  renderRecipientInstitutionDetails,
  onBack,
  onSubmit,
  showAdditionalDetailsScreen,
  setShowAdditionalDetailsScreen,
}: FreeFormFxProps) => {
  const { l10n } = useLocalization();
  const { featureEnabled } = useFeature();
  const { accounts } = useContext(AccountContext);
  const { control, watch, trigger, setValue, setError, getValues } =
    useCreateInternationalWireForm();
  const recipients = useRecipients();
  const limits = useLimits();
  const limitsForDisplay = useMemo(
    () =>
      getLimitsForDisplay(limits, [
        "international_wire",
        "organization_user_international_wire",
      ]),
    [limits]
  );
  const validAccounts = accounts.filter(
    (a: Account) => a.isInternal() && a.isValidTransferSource()
  );
  const {
    templateSelected,
    requiresAmount,
    requiresMemo,
    requiresWireReason,
    requiresNote,
  } = modules.internationalWires.useConditionalDetails();
  const showSchedulingControls = featureEnabled("international_wires_consumer");
  const startDate = watch("startDate");
  const recurringRule = watch("recurringRule");
  const effectiveConversionRate = useEffectiveConversionRate();
  const {
    fetchExchangeRateQuote,
    loading: exchangeRateLoading,
    errorMessage: exchangeRateError,
  } = useFetchExchangeRateQuote();

  const [exchangeRateData, setExchangeRateData] =
    useState<API.FetchExchangeRateQuote.Response | null>(null);
  const watchedAmount = useWatch({ control, name: "amount" });
  const watchedCurrencyPreference = useWatch({
    control,
    name: "currencyTransferPreference",
  });
  const watchedRecipientCurrency = useWatch({
    control,
    name: "recipientCurrency",
  });

  const generateQuoteRequestKey = (
    amount: string,
    currencyPref: string,
    recipientCurr: string
  ): string => `${amount}|${currencyPref}|${recipientCurr}`;


  const getCurrencyPreferenceFromQuote = (
    quote: API.FetchExchangeRateQuote.Response
  ): "send" | "receive" =>
    quote.specified_currency === "USD" ? "send" : "receive";

  const getQuoteRequestKey = (
    quote: API.FetchExchangeRateQuote.Response
  ): string => {
    const preference = getCurrencyPreferenceFromQuote(quote);
    return generateQuoteRequestKey(
      quote.amount,
      preference,
      quote.converted_currency
    );
  };

  const fetchExchangeRate = async (
    amount: string,
    currencyPreference: string,
    recipientCurrency: string
  ) => {
    setExchangeRateData(null);
    
    if (!amount || !recipientCurrency || parseFloat(amount) <= 0) {
      return;
    }

    const isReceivePreference = currencyPreference === "receive";

    const result = await fetchExchangeRateQuote(
      isReceivePreference ? recipientCurrency : "USD",
      amount,
      isReceivePreference ? undefined : recipientCurrency
    );

    if (result) {
      // Verify the response still matches the current form state
      const resultRequestKey = getQuoteRequestKey(result);
      const formValues = getValues();
      const nowRequestKey = generateQuoteRequestKey(
        String(formValues.amount || ""),
        formValues.currencyTransferPreference || "",
        formValues.recipientCurrency || ""
      );

      if (
        resultRequestKey === nowRequestKey ||
        (exchangeRateData && getQuoteRequestKey(exchangeRateData) === nowRequestKey)
      ) {
        if (resultRequestKey === nowRequestKey) {
          setExchangeRateData(result);
          setValue("quote", result);
        }
      } else {
        setExchangeRateData(null);
        setValue("quote", undefined);
      }
      trigger("amount");
    } else {
      setExchangeRateData(null);
      setValue("quote", undefined);
      trigger("amount");
    }
  };

  const debouncedFetchExchangeRate = useDebounce(
    (amt: string, currPref: string, recipCurr: string) => {
      fetchExchangeRate(amt, currPref, recipCurr);
    },
    300
  );

  useEffect(() => {
    if (
      watchedAmount &&
      parseFloat(String(watchedAmount)) > 0 &&
      watchedCurrencyPreference &&
      watchedRecipientCurrency
    ) {
      debouncedFetchExchangeRate(
        String(watchedAmount),
        watchedCurrencyPreference,
        watchedRecipientCurrency
      );
    } else {
      setExchangeRateData(null);
      setValue("quote", undefined);
    }
  }, [
    watchedAmount,
    watchedCurrencyPreference,
    watchedRecipientCurrency,
    debouncedFetchExchangeRate,
  ]);

  const renderWireReasons = () => {
    const reasons = Object.values(reasonsRes)?.[0];
    if (reasons?.length) {
      return (
        <div
          className={cc({
            "margin--bottom--l": requiresNote,
          })}
        >
          <Controller
            name={"wireReason"}
            control={control as any}
            render={({
              field: { onChange: controlOnChange, value },
              fieldState: { error },
            }) => {
              const onChange = (e: string) => {
                controlOnChange(e);
                setValue(
                  "wireReasonDescription",
                  filters.humanize(
                    reasons.find((reason) => reason.code === e)?.description ||
                      ""
                  )
                );
              };
              return (
                <WireReasonSelector
                  reasons={reasons}
                  onChange={onChange}
                  value={value}
                  label={l10n.getString("label-wire-reason")}
                  error={error?.message || ""}
                />
              );
            }}
          />
        </div>
      );
    }
    return null;
  };

  const amountField = (
    <div className="margin--bottom--l">
      <CurrencyAmountInput
        name="amount"
        control={control as any}
        label={
          watch("currencyTransferPreference") === "receive"
            ? l10n.getString("label-currency-amount-receive-exactly")
            : l10n.getString("label-currency-amount-send-exactly")
        }
        currency={
          watch("currencyTransferPreference") === "receive"
            ? watch("recipientCurrency") || "USD"
            : "USD"
        }
      />
    </div>
  );

  const memoTooltip = (
    <Tooltip text={l10n.getString("tooltip-international-wire-memo-statement")}>
      <span className="narmi-icon-info" />
    </Tooltip>
  );

  const memoField = (
    <div className="margin--bottom--l">
      <Controller
        name={"memo"}
        control={control as any}
        render={({
          field: { onChange, onBlur, value, ref },
          fieldState: { error },
        }) => (
          <TextInput
            field="memo"
            onBlur={onBlur}
            ref={ref}
            label={l10n.getString("label-memo-optional")}
            endContent={memoTooltip}
            onChange={onChange}
            value={value ?? ""}
            error={error?.message}
          />
        )}
      />
    </div>
  );

  const noteField = (
    <Controller
      name={"note"}
      control={control as any}
      render={({
        field: { onChange, onBlur, value, ref },
        fieldState: { error },
      }) => (
        <TextInput
          field="note"
          onBlur={onBlur}
          ref={ref}
          label={l10n.getString("label-internal-note-optional")}
          onChange={onChange}
          value={value ?? ""}
          error={error?.message}
        />
      )}
    />
  );

  const schedulingFields = showSchedulingControls ? (
    <>
      <div className="margin--bottom--l">
        <InternationalWireStartDateField
          control={control as any}
          label={l10n.getString("label-date-once")}
          setValue={setValue}
        />
      </div>
      <div
        className={
          recurringRule?.isVisible ? "margin--bottom--s" : "margin--bottom--l"
        }
      >
        <Controller
          name={"recurringRule.frequency" as any}
          control={control as any}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FrequencySelector
              label={l10n.getString("label-frequency")}
              value={value || FREQUENCIES.ONCE}
              error={error?.message}
              onChange={(frequency: string) =>
                handleInternationalWireFrequencyChange(
                  frequency,
                  startDate,
                  onChange,
                  setValue
                )
              }
            />
          )}
        />
      </div>
      {recurringRule?.isVisible && (
        <div className={styles.recurrenceEndCard}>
          <InternationalWireRecurrenceEndCard
            amount={watchedAmount}
            count={recurringRule.count}
            control={control}
            ends={recurringRule.ends}
            frequency={recurringRule.frequency}
            startDate={startDate}
            setValue={setValue}
            untilDate={recurringRule.untilDate}
          />
        </div>
      )}
    </>
  ) : null;

  const renderCurrencyDetailsCard = () => (
    <ContentCard kind="bordered">
      <p className={styles.wireContainerSubHeader}>
        {l10n.getString("international-wire-form-currency-subheader")}
      </p>
      <div className="margin--bottom--xl">
        <Controller
          name="currencyTransferPreference"
          control={control as any}
          render={({ field: { onChange, value } }) => (
            <RadioButtons
              name="type"
              options={{
                [l10n.getString("label-currency-radio-option-receives", {
                  currency: watch("recipientCurrency") || "",
                })]: "receive",
                [l10n.getString("label-currency-radio-option-send")]: "send",
              }}
              initialValue={value}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                onChange(e.target.value);
              }}
            />
          )}
        />
      </div>
      <p className={styles.wireContainerSubHeader}>
        {l10n.getString("label-amount")}
      </p>
      {amountField}
      {exchangeRateError && (
        <div className={styles.fxAlertContainer}>
          <Alert isActive kind="error" isDismissable={false} paddingSize="s">
            {exchangeRateError}
          </Alert>
        </div>
      )}
      {(![null, undefined].includes(
        exchangeRateData?.converted_amount as any
      ) ||
        exchangeRateLoading) && (
        <>
          <div className="margin--bottom--xl">
            <CurrencyAmountInput
              label={
                watch("currencyTransferPreference") === "receive"
                  ? l10n.getString("label-currency-amount-send-estimate")
                  : l10n.getString("label-currency-amount-receive-estimate")
              }
              value={exchangeRateData?.converted_amount || "0"}
              currency={
                watch("currencyTransferPreference") === "receive"
                  ? "USD"
                  : watch("recipientCurrency") || "USD"
              }
              disabled
            />
          </div>
          <Row
            alignItems="center"
            justifyContent="space-between"
            className="margin--bottom--xs"
          >
            <Row.Item shrink>
              <h5 className="fontSize--s fontWeight--bold">
                {l10n.getString(
                  "international-wire-form-currency-estimated-exchange-rate"
                )}
              </h5>
            </Row.Item>
            {exchangeRateLoading ? (
              <Row.Item shrink>
                <Spinner size={22} color="var(--theme-secondary)" />
              </Row.Item>
            ) : (
              <Row.Item shrink>
                {effectiveConversionRate
                  ? `1 USD = ${effectiveConversionRate.toFixed(4)} ${
                      exchangeRateData?.converted_currency === "USD"
                        ? exchangeRateData?.specified_currency
                        : exchangeRateData?.settlement_currency
                    }`
                  : ""}
              </Row.Item>
            )}
          </Row>
          <div className={styles.fxAlertContainer}>
            <Alert isActive kind="info" isDismissable={false} paddingSize="s">
              {l10n.getString("alert-exchange-rate")}
            </Alert>
          </div>
        </>
      )}
    </ContentCard>
  );

  /* ---------- Template mode ---------- */
  if (templateSelected) {
    const hasOtherGapFields =
      requiresMemo || requiresWireReason || requiresNote;

    return (
      <>
        {requiresAmount && (
          <ResponsiveFlex gapSize="m">
            {renderCurrencyDetailsCard()}
          </ResponsiveFlex>
        )}
        {hasOtherGapFields && (
          <ContentCard kind="bordered">
            <p className={styles.wireContainerSubHeader}>
              {l10n.getString("international-wire-form-subheader")}
            </p>
            {requiresMemo && memoField}
            {requiresWireReason && renderWireReasons()}
            {requiresNote && noteField}
            <LimitsDialog
              type={["international_wire"]}
              limitsForDisplay={limitsForDisplay}
            />
          </ContentCard>
        )}
        {!hasOtherGapFields && (
          <LimitsDialog
            type={["international_wire"]}
            limitsForDisplay={limitsForDisplay}
          />
        )}
      </>
    );
  }

  /* ---------- Freeform mode ---------- */

  if (showAdditionalDetailsScreen) {
    return (
      <>
        <ContentCard key="fx-additional-details-step" kind="bordered">
          <p className={styles.wireContainerSubHeader}>
            {l10n.getString("header-transfer-additional-details")}
          </p>
          {schedulingFields}
          {memoField}
          {renderWireReasons()}
          {noteField}
        </ContentCard>
        <Row alignItems="center" justifyContent="end">
          <Row.Item>
            <Button
              label={l10n.getString("button-back")}
              kind="negative"
              type="button"
              onClick={() => setShowAdditionalDetailsScreen(false)}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={l10n.getString("button-next")}
              onClick={async () => {
                const isValid = await trigger([
                  "startDate",
                  "recurringRule",
                  "memo",
                  "note",
                  "wireReason",
                ]);
                if (isValid && !exchangeRateLoading) {
                  onSubmit();
                }
              }}
            />
          </Row.Item>
        </Row>
      </>
    );
  }

  return (
    <>
      <ContentCard key="fx-source-and-recipient-step" kind="bordered">
        <p className={styles.wireContainerSubHeader}>
          {l10n.getString("international-wire-form-subheader")}
        </p>
        <div className="margin--bottom--l">
          <Controller
            name={"fromAccountId"}
            control={control as any}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <ComboAccountSelector
                value={value}
                isDestination={false}
                onChange={onChange}
                label={l10n.getString("label-from")}
                accounts={validAccounts}
                showAddExternalAccountLink={false}
                errorText={error?.message}
              />
            )}
          />
        </div>
        <Controller
          name={"recipientId"}
          control={control as any}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <RecipientSelector
              onChange={(recipientId) => {
                onChange(recipientId);
              }}
              value={value}
              label={l10n.getString("label-to")}
              recipients={recipients}
              onAddRecipientClick={onAddRecipientClick}
              error={error?.message}
              displayBottomAction
            />
          )}
        />
        {renderRecipientInstitutionDetails()}
      </ContentCard>

      <ResponsiveFlex gapSize="m">
        {renderCurrencyDetailsCard()}
        <div>
          <LimitsDialog
            type={["international_wire"]}
            limitsForDisplay={limitsForDisplay}
          />
        </div>
      </ResponsiveFlex>
      <Row alignItems="center">
        <Row.Item>
          <Button
            label={l10n.getString("button-back")}
            kind="negative"
            onClick={onBack}
            type="button"
          />
        </Row.Item>
        <Row.Item shrink>
          <Button
            label={l10n.getString("button-next")}
            onClick={async () => {
              const isValid = await trigger([
                "fromAccountId",
                "recipientId",
                "amount",
                "currencyTransferPreference",
              ]);
              if (!isValid || exchangeRateLoading) {
                return;
              }

              const formValues = watch();
              const validation = validateAmountAgainstBalance(
                formValues,
                validAccounts,
                l10n
              );
              if (!validation.isValid) {
                setError("amount", {
                  type: "manual",
                  message: validation.error!,
                });
                return;
              }

              setShowAdditionalDetailsScreen(true);
            }}
          />
        </Row.Item>
      </Row>
    </>
  );
};

export default FreeFormFx;
